import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Stack,
  Grid,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  ShoppingBag,
  LocationOn,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { getAdminCartItems } from '../../services/cartService';
import { getUserAddresses, createAdminOrders } from '../../services/orderService';
import { getCurrencyRates } from '../../services/currency';
import { settingsService } from '../../services/settingsService';
import { calculateBulkShippingFees } from '../../services/shippingService';

/**
 * Currency conversion utility
 */
const currency_converter = (defaultCurrency, preferredCurrency, price, rate) => {
  if (defaultCurrency === preferredCurrency) {
    return price;
  }
  if (defaultCurrency === 'NGN') {
    return price / rate;
  }
  return price * rate;
};

/**
 * Round number to 2 decimal places
 */
const round_to_two = (number) => {
  if (number == null || isNaN(number) || !isFinite(number)) {
    return '0.00';
  }
  const result = +(Math.round(Number(number) * 100) / 100).toFixed(2);
  return result.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
};

const toNumber = (value, fallback = 0) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizePricingTier = (pricingTierRaw) => {
  if (!pricingTierRaw) return [];

  let tiers = pricingTierRaw;
  if (typeof tiers === 'string') {
    try {
      tiers = JSON.parse(tiers);
    } catch (error) {
      try {
        tiers = JSON.parse(tiers.replace(/}\s*{/g, '}, {'));
      } catch {
        tiers = [];
      }
    }
  }

  if (Array.isArray(tiers)) return tiers;
  if (typeof tiers === 'object') return [tiers];
  return [];
};

const resolvePricingParams = (basePrice, pricingSettings) => {
  const tiers = normalizePricingTier(pricingSettings?.pricing_tier);
  for (const tier of tiers) {
    const min = toNumber(tier?.min, 0);
    const max = tier?.max !== null && tier?.max !== undefined ? toNumber(tier.max, Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
    if (basePrice >= min && basePrice <= max) {
      return {
        percentage: Math.max(0, toNumber(tier?.percent, 0)),
        minFee: Math.max(0, toNumber(tier?.minFee, 0)),
      };
    }
  }

  return {
    percentage: Math.max(0, toNumber(pricingSettings?.service_fee_percentage, 0)),
    minFee: Math.max(0, toNumber(pricingSettings?.minimum_service_fee, 0)),
  };
};

const applyPricingMode = (amount, pricingSettings) => {
  const basePrice = toNumber(amount, 0);
  if (basePrice <= 0) {
    return { price: 0, service_fee: 0, service_fee_percentage: 0 };
  }

  const { percentage, minFee } = resolvePricingParams(basePrice, pricingSettings);
  const percentageFee = basePrice * (percentage / 100);
  const serviceFee = Math.max(percentageFee, minFee);
  const displayMode = (pricingSettings?.price_display_mode || 'separated').toLowerCase();
  const price = displayMode === 'inclusive' ? basePrice + serviceFee : basePrice;

  return { price, service_fee: serviceFee, service_fee_percentage: percentage };
};

const applyPricingModeForLine = (unitPrice, quantity, pricingSettings) => {
  const baseUnit = toNumber(unitPrice, 0);
  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const lineBaseTotal = baseUnit * qty;
  const result = applyPricingMode(lineBaseTotal, pricingSettings);

  return {
    final_unit_price: result.price / qty,
    final_line_total: result.price,
    service_fee: result.service_fee,
    service_fee_percentage: result.service_fee_percentage,
  };
};

const Checkout = () => {
  const { t } = useTranslation(['cart', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get cart item IDs from URL - memoized to prevent infinite loops
  const itemsParam = searchParams.get('items') || '';
  const cartItemIds = useMemo(() => {
    if (!itemsParam) return [];
    return itemsParam.split(',').filter(id => id.trim()).map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
  }, [itemsParam]);
  
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [remarks, setRemarks] = useState({});
  const [currencyRate, setCurrencyRate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [shippingSettings, setShippingSettings] = useState(null);
  const [calculatingShipping, setCalculatingShipping] = useState(false);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const isInclusivePricing = (shippingSettings?.price_display_mode || 'separated').toLowerCase() === 'inclusive';
  const prefetchedShippingSettings = location.state?.prefetchedShippingSettings || null;
  
  // Group cart items by user
  const itemsByUser = cartItems.reduce((acc, item) => {
    const userId = item.user?.id;
    if (!userId) return acc;
    if (!acc[userId]) {
      acc[userId] = {
        user: item.user,
        items: [],
      };
    }
    acc[userId].items.push(item);
    return acc;
  }, {});
  
  const users = Object.values(itemsByUser);
  
  // Fetch currency rate and shipping settings (cached)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (prefetchedShippingSettings) {
          setShippingSettings({
            initial_shipping_fee: toNumber(prefetchedShippingSettings.initial_shipping_fee, 0),
            minimum_service_fee: toNumber(prefetchedShippingSettings.minimum_service_fee, 0),
            service_fee_percentage: toNumber(prefetchedShippingSettings.service_fee_percentage, 0),
            price_display_mode: String(prefetchedShippingSettings.price_display_mode || 'separated').trim().toLowerCase(),
            pricing_tier: prefetchedShippingSettings.pricing_tier || [],
            area_id: prefetchedShippingSettings.area_id
          });
        }

        // Fetch currency rate
        const response = await getCurrencyRates({ page: 1, page_size: 100 });
        const rates = response.results || [];
        const ngnToCnyRate = rates.find(rate => 
          rate.base_currency_code === 'NGN' && rate.target_currency_code === 'CNY'
        );
        if (ngnToCnyRate && ngnToCnyRate.base_currency_rate) {
          setCurrencyRate({
            rate: parseFloat(ngnToCnyRate.base_currency_rate),
            symbol: ngnToCnyRate.base_currency_symbol || '₦',
          });
        }
        
        // Fetch shipping settings (will use cache if available)
        const settings = await settingsService.getShippingSettings(true);
        const shippingSettingsData = {
          initial_shipping_fee: toNumber(settings.initial_shipping_fee, 0),
          minimum_service_fee: toNumber(settings.minimum_service_fee, 0),
          service_fee_percentage: toNumber(settings.service_fee_percentage, 0),
          price_display_mode: String(settings.price_display_mode || 'separated').trim().toLowerCase(),
          pricing_tier: settings.pricing_tier || [],
          area_id: settings.area_id
        };
        console.log('Shipping settings loaded:', shippingSettingsData);
        setShippingSettings(shippingSettingsData);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        // Don't set defaults - let the component handle the error state
        showSnackbar('Failed to load shipping settings', 'error');
      }
    };
    fetchInitialData();
  }, [prefetchedShippingSettings]);
  
  // Format price helper
  const formatPrice = (price) => {
    if (!currencyRate) {
      return `¥${parseFloat(price || 0).toFixed(2)}`;
    }

    if (!price) {
      return `₦${parseFloat(price || 0).toFixed(2)}`;
    }

    const convertedPrice = currency_converter('CNY', 'NGN', parseFloat(price), currencyRate.rate);
    return `${currencyRate.symbol}${round_to_two(convertedPrice)}`;
  };
  
  // Load cart items
  useEffect(() => {
    const loadCartItems = async () => {
      if (cartItemIds.length === 0) {
        setError('No cart items selected');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all cart items (we'll filter by IDs on frontend)
        const response = await getAdminCartItems({ page: 1, page_size: 1000 });
        
        if (response.status === 'success') {
          // Filter to only selected items
          const selectedItems = (response.results || []).filter(item => 
            cartItemIds.includes(item.id)
          );
          
          if (selectedItems.length === 0) {
            setError('Selected cart items not found');
            setLoading(false);
            return;
          }
          
          // Check if all items belong to the same user
          const userIds = [...new Set(selectedItems.map(item => item.user?.id).filter(Boolean))];
          if (userIds.length > 1) {
            setError('Selected items belong to different users. Please select items from the same user.');
            setLoading(false);
            return;
          }
          
          setCartItems(selectedItems);
          setShippingCalculated(false); // Reset flag when new items are loaded
          
          // Load addresses for the user
          if (userIds.length === 1) {
            await loadUserAddresses(userIds[0]);
          }
          
          // Note: Shipping calculation will be triggered by useEffect when both cartItems and shippingSettings are ready
        } else {
          setError('Failed to load cart items');
        }
      } catch (error) {
        console.error('Error loading cart items:', error);
        setError('Failed to load cart items: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };
    
    loadCartItems();
  }, [itemsParam]); // Use the URL parameter string directly to prevent infinite loops
  
  // Load user addresses
  const loadUserAddresses = async (userId) => {
    try {
      const response = await getUserAddresses(userId);
      if (response.status === 'success') {
        setAddresses(response.addresses || []);
        // Select default address if available
        const defaultAddress = response.addresses?.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        } else if (response.addresses?.length > 0) {
          setSelectedAddressId(response.addresses[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
      showSnackbar('Failed to load addresses', 'error');
    }
  };

  // Calculate shipping fees when both cartItems and shippingSettings are ready
  useEffect(() => {
    // Only calculate if we have items, settings, not currently calculating, and haven't calculated yet
    if (cartItems.length > 0 && shippingSettings && !calculatingShipping && !shippingCalculated) {
      console.log('Triggering shipping calculation:', { 
        itemsCount: cartItems.length, 
        hasSettings: !!shippingSettings,
        settingsAreaId: shippingSettings?.area_id,
        calculating: calculatingShipping,
        alreadyCalculated: shippingCalculated
      });
      calculateShippingFeesForItems(cartItems);
      setShippingCalculated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length, shippingSettings?.area_id]); // Re-run when cart items or settings change

  /**
   * Transform cart items to shipping calculation format
   */
  const transformCartItemsForShipping = (items) => {
    return items.map(item => {
      const skus = item.sku || item.skus || [];
      const skuArray = Array.isArray(skus) ? skus : (skus ? [skus] : []);
      
      // Build sku_data object keyed by SKU pk ID
      const sku_data = {};
      skuArray.forEach(sku => {
        const skuPkId = sku.id || sku.sku_id; // Use database pk ID
        if (skuPkId) {
          sku_data[skuPkId] = {
            price: parseFloat(sku.price || 0),
            qty: parseInt(sku.quantity || 0),
            total_price: parseFloat(sku.total_price || 0),
            sku_id: sku.sku_id || skuPkId, // OneBound SKU ID
            properties: sku.properties || '',
            properties_name: sku.properties_name || '',
            url: sku.url || item.pic_url || '',
            service_fee: parseFloat(sku.service_fee || 0)
          };
        }
      });
      
      return {
        num_iid: item.item_id || item.num_iid,
        sku_data,
        area_id: shippingSettings?.area_id || '370000', // Default area_id if settings not loaded yet
        site: '1688'
      };
    });
  };

  /**
   * Calculate shipping fees for cart items
   */
  const calculateShippingFeesForItems = async (items) => {
    if (!shippingSettings || items.length === 0) {
      return;
    }
    
    // Prevent multiple simultaneous calculations
    if (calculatingShipping) {
      return;
    }
    
    try {
      setCalculatingShipping(true);
      
      // Transform items to shipping format
      const shippingItems = transformCartItemsForShipping(items);
      
      // Call shipping calculation API
      const response = await calculateBulkShippingFees(shippingItems);
      
      if (response.success && response.items) {
        // Update cart items with calculated shipping fees
        const updatedItems = items.map(item => {
          // Find matching shipping result by num_iid
          const shippingResult = response.items.find(
            result => result.num_iid === (item.item_id || item.num_iid)
          );
          
          if (shippingResult && shippingResult.success) {
            return {
              ...item,
              post_fee: parseFloat(shippingResult.shipping_fee || 0)
            };
          } else {
            // Use manual shipping fee if API failed for this item
            return {
              ...item,
              post_fee: parseFloat(item.post_fee || (shippingSettings?.initial_shipping_fee || 0))
            };
          }
        });
        
        setCartItems(updatedItems);
      } else {
        // API failed, use manual shipping fees
        const updatedItems = items.map(item => ({
          ...item,
          post_fee: parseFloat(item.post_fee || shippingSettings?.initial_shipping_fee || 0)
        }));
        setCartItems(updatedItems);
      }
    } catch (error) {
      console.error('Error calculating shipping fees:', error);
      // Use manual shipping fees on error
      const updatedItems = items.map(item => ({
        ...item,
        post_fee: parseFloat(item.post_fee || shippingSettings?.initial_shipping_fee || 0)
      }));
      setCartItems(updatedItems);
    } finally {
      setCalculatingShipping(false);
    }
  };

  // Check if all shipping fees are ready
  const areShippingFeesReady = () => {
    if (cartItems.length === 0) return false;
    if (calculatingShipping) return false;
    // Check if all items have post_fee set (either from API or manual calculation)
    return cartItems.every(item => 
      item.post_fee !== undefined && item.post_fee !== null
    );
  };

  // Calculate totals from SKU prices
  const calculateTotals = () => {
    let subtotal = 0;
    let inclusiveSubtotal = 0;
    let totalServiceFee = 0;
    let totalPostFee = 0;
    
    cartItems.forEach(item => {
      const skus = item.sku || item.skus || [];
      const skuArray = Array.isArray(skus) ? skus : (skus ? [skus] : []);
      
      if (skuArray.length > 0) {
        const totalQuantity = skuArray.reduce((sum, sku) => sum + (parseInt(sku.quantity, 10) || 0), 0);
        skuArray.forEach(sku => {
          const quantity = parseInt(sku.quantity, 10) || 0;
          const unitBasePrice = toNumber(sku.price, 0);
          const lineBaseTotal = unitBasePrice * quantity;
          const pricingResult = applyPricingModeForLine(unitBasePrice, totalQuantity || 1, shippingSettings);
          const unitServiceFee = pricingResult.service_fee / (totalQuantity || 1);
          const skuServiceFee = unitServiceFee * quantity;
          const skuInclusiveTotal = pricingResult.final_unit_price * quantity;

          subtotal += lineBaseTotal;
          inclusiveSubtotal += skuInclusiveTotal;
          totalServiceFee += skuServiceFee;
        });
      } else {
        const basePrice = toNumber(item.price, 0);
        const pricingResult = applyPricingMode(basePrice, shippingSettings);
        subtotal += basePrice;
        inclusiveSubtotal += basePrice + pricingResult.service_fee;
        totalServiceFee += pricingResult.service_fee;
      }
      
      totalPostFee += toNumber(item.post_fee, 0);
    });

    const total = (isInclusivePricing ? inclusiveSubtotal : subtotal + totalServiceFee) + totalPostFee;
    
    return {
      subtotal,
      inclusiveSubtotal,
      serviceFee: totalServiceFee,
      postFee: totalPostFee,
      total,
    };
  };
  
  const totals = calculateTotals();
  
  // Handle order submission
  const handleSubmitOrder = async () => {
    if (cartItems.length === 0) {
      showSnackbar('No items to order', 'error');
      return;
    }
    
    if (!selectedAddressId && addresses.length > 0) {
      showSnackbar('Please select a delivery address', 'error');
      return;
    }
    
    // Check if all items belong to same user
    const userIds = [...new Set(cartItems.map(item => item.user?.id).filter(Boolean))];
    if (userIds.length !== 1) {
      showSnackbar('All items must belong to the same user', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      const cartPostFees = cartItems.reduce((acc, item) => {
        if (item?.id !== undefined && item?.id !== null) {
          acc[item.id] = toNumber(item.post_fee, 0);
        }
        return acc;
      }, {});
      
      const orderData = {
        user_id: userIds[0],
        cart_ids: cartItemIds,
        receiver_address_id: selectedAddressId || null,
        remarks: remarks,
        cart_post_fees: cartPostFees,
      };
      
      const response = await createAdminOrders(orderData);
      
      if (response.status === 'success') {
        showSnackbar(
          `Successfully created ${response.orders?.length || 0} order(s). Orders are pending approval.`,
          'success'
        );
        
        // Navigate back to cart after 2 seconds
        setTimeout(() => {
          navigate('/admin/cart');
        }, 2000);
      } else {
        showSnackbar(response.message || 'Failed to create orders', 'error');
      }
    } catch (error) {
      console.error('Error creating orders:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to create orders: ' + error.message,
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/admin/cart')} startIcon={<ArrowBack />}>
          Back to Cart
        </Button>
      </Box>
    );
  }
  
  if (cartItems.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No cart items found
        </Alert>
        <Button variant="contained" onClick={() => navigate('/admin/cart')} startIcon={<ArrowBack />}>
          Back to Cart
        </Button>
      </Box>
    );
  }
  
  const currentUser = users[0]?.user;
  
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/cart')}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            Checkout
          </Typography>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        {/* Left Column - Order Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* User Info */}
          {currentUser && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Typography variant="body1">
                  <strong>Username:</strong> {currentUser.username || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Phone:</strong> {currentUser.phone || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong> {currentUser.email || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          )}
          
          {/* Address Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn /> Delivery Address
              </Typography>
              
              {addresses.length > 0 ? (
                <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                  <RadioGroup
                    value={selectedAddressId || ''}
                    onChange={(e) => setSelectedAddressId(parseInt(e.target.value))}
                  >
                    {addresses.map((address) => (
                      <FormControlLabel
                        key={address.id}
                        value={address.id}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {address.receiver_name}
                              {address.is_default && (
                                <Chip label="Default" size="small" sx={{ ml: 1 }} color="primary" />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {address.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {address.receiver_phone}
                            </Typography>
                            {address.city && (
                              <Typography variant="body2" color="text.secondary">
                                {address.city}{address.state ? `, ${address.state}` : ''}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No addresses found. System warehouse address will be used.
                </Alert>
              )}
            </CardContent>
          </Card>
          
          {/* Order Items */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Items ({cartItems.length})
              </Typography>
              
              <Stack spacing={2} sx={{ mt: 2 }}>
                {cartItems.map((item) => (
                  <Paper key={item.id} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'flex-start' }
                    }}>
                      <Avatar
                        src={item.pic_url}
                        variant="rounded"
                        sx={{ 
                          width: { xs: '100%', sm: 120 }, 
                          height: { xs: 200, sm: 120 }, 
                          flexShrink: 0,
                          maxWidth: { xs: '100%', sm: 120 }
                        }}
                      >
                        <ShoppingBag />
                      </Avatar>
                      <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                        <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Item ID: {item.item_id}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                        
                        {/* Display SKUs with prices */}
                        {(() => {
                          const skus = item.sku || item.skus || [];
                          const skuArray = Array.isArray(skus) ? skus : (skus ? [skus] : []);
                          
                          if (skuArray.length > 0) {
                            const itemQuantity = skuArray.reduce((sum, sku) => sum + (parseInt(sku.quantity) || 0), 0);
                            const itemBaseSubtotal = skuArray.reduce((sum, sku) => {
                              const unitPrice = toNumber(sku.price, 0);
                              const qty = parseInt(sku.quantity, 10) || 0;
                              return sum + (unitPrice * qty);
                            }, 0);
                            const itemServiceFee = skuArray.reduce((sum, sku) => {
                              const qty = parseInt(sku.quantity, 10) || 0;
                              const unitPrice = toNumber(sku.price, 0);
                              const linePricing = applyPricingModeForLine(unitPrice, itemQuantity || 1, shippingSettings);
                              const unitServiceFee = linePricing.service_fee / (itemQuantity || 1);
                              return sum + (unitServiceFee * qty);
                            }, 0);
                            const itemInclusiveSubtotal = itemBaseSubtotal + itemServiceFee;
                            const itemDisplayedSubtotal = isInclusivePricing ? itemInclusiveSubtotal : itemBaseSubtotal;
                            
                            return (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  SKUs ({skuArray.length}):
                                </Typography>
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Image</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Specification</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Price</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {skuArray.map((sku, index) => (
                                        <TableRow key={sku.id || sku.sku_id || index}>
                                          <TableCell>
                                            <Avatar
                                              src={sku.url || item.pic_url}
                                              variant="rounded"
                                              sx={{ width: 40, height: 40 }}
                                            >
                                              <ShoppingBag />
                                            </Avatar>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {(() => {
                                                const propsName = sku.properties_name || sku.properties || '';
                                                if (!propsName) return 'No specification';
                                                
                                                // Remove the index prefix (e.g., "0:0:" or "1:3:") from the beginning
                                                // Format: "0:0: Color: M10 black [TFT screen + dual payment]"
                                                // Result: "Color: M10 black [TFT screen + dual payment]"
                                                const cleaned = propsName.replace(/^\d+:\d+:\s*/, '');
                                                return cleaned || propsName;
                                              })()}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {(() => {
                                                const unitPrice = toNumber(sku.price, 0);
                                                const linePricing = applyPricingModeForLine(unitPrice, itemQuantity || 1, shippingSettings);
                                                const displayedUnitPrice = isInclusivePricing ? linePricing.final_unit_price : unitPrice;
                                                return formatPrice(displayedUnitPrice);
                                              })()}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {sku.quantity || 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                              {(() => {
                                                const unitPrice = toNumber(sku.price, 0);
                                                const qty = parseInt(sku.quantity, 10) || 0;
                                                const linePricing = applyPricingModeForLine(unitPrice, itemQuantity || 1, shippingSettings);
                                                const displayedTotal = isInclusivePricing ? linePricing.final_unit_price * qty : unitPrice * qty;
                                                return formatPrice(displayedTotal);
                                              })()}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            Item Total:
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {itemQuantity}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {formatPrice(itemDisplayedSubtotal)}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                <Box sx={{ mt: 1, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    mb: 0.5,
                                    flexWrap: 'wrap',
                                    gap: 1
                                  }}>
                                    <Typography variant="body2">
                                      <strong>Total Quantity:</strong> {itemQuantity}
                                    </Typography>
                                    <Typography variant="body2" color="primary">
                                      <strong>Subtotal:</strong> {formatPrice(itemDisplayedSubtotal)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'flex-end', 
                                    alignItems: 'center',
                                    mt: 0.5,
                                    gap: 1
                                  }}>
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>Shipping Fee:</strong>
                                    </Typography>
                                    {calculatingShipping || item.post_fee === undefined || item.post_fee === null ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CircularProgress size={14} />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                          Calculating...
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        {formatPrice(parseFloat(item.post_fee) || 0)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            );
                          } else {
                            // Fallback to item price if no SKUs
                            const basePrice = toNumber(item.price, 0);
                            const fallbackQuantity = Math.max(1, parseInt(item.min_num, 10) || 1);
                            const fallbackLine = applyPricingModeForLine(basePrice, fallbackQuantity, shippingSettings);
                            const displayedPrice = isInclusivePricing ? fallbackLine.final_unit_price : basePrice;
                            return (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Price: {formatPrice(displayedPrice)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Shipping Fee:</strong>
                                  </Typography>
                                  {calculatingShipping || item.post_fee === undefined || item.post_fee === null ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CircularProgress size={14} />
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        Calculating...
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      {formatPrice(parseFloat(item.post_fee) || 0)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          }
                        })()}
                        
                        <TextField
                          fullWidth
                          size="small"
                          label="Remarks (Optional)"
                          value={remarks[item.id] || ''}
                          onChange={(e) => setRemarks({ ...remarks, [item.id]: e.target.value })}
                          sx={{ mt: 1 }}
                        />
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right Column - Order Summary */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">{formatPrice(isInclusivePricing ? totals.inclusiveSubtotal : totals.subtotal)}</Typography>
              </Box>
              
              {!isInclusivePricing && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">Service Fee:</Typography>
                  <Typography variant="body1">{formatPrice(totals.serviceFee)}</Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Shipping Fee:</Typography>
                <Typography variant="body1">{formatPrice(totals.postFee)}</Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(totals.total)}
                </Typography>
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={handleSubmitOrder}
                disabled={submitting || cartItems.length === 0 || !areShippingFeesReady()}
                sx={{
                  backgroundColor: '#DC143C',
                  '&:hover': { backgroundColor: '#B91C3C' },
                  '&:disabled': { backgroundColor: '#cccccc' },
                  py: 1.5,
                }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    Creating Orders...
                  </>
                ) : !areShippingFeesReady() ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    Calculating Shipping Fees...
                  </>
                ) : (
                  'Create Orders'
                )}
              </Button>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                Orders will be created with status "Pending Approval". A superior admin must approve them before payment is deducted.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Checkout;

